import i18next from "i18next";
import { store } from "../app/store";
import { setCulture } from "../features/hotel/hotelSlice";
import { languages, type ILanguage } from "../config/languages";

const languageCodeToI18n = (languageCode: string) =>
  languageCode.split("-")[0] || "en";

export const syncI18nWithCulture = (languageCode: string) => {
  const next = languageCodeToI18n(languageCode);
  if (languageCodeToI18n(i18next.language || "") === next) return;
  i18next.changeLanguage(next);
};

/**
 * Keep Redux culture and i18n in sync.
 * When they differ, prefer the browser-detected i18n language if available for the hotel.
 */
export const syncCultureWithI18n = (hotelCode: string) => {
  if (!hotelCode) return;

  const { hotel } = store.getState();
  const availableLanguages = hotelLanguages(hotelCode);
  const cultureLanguageCode = hotel.Culture.LanguageCode;
  const cultureLang = languageCodeToI18n(cultureLanguageCode);
  const i18nLang = languageCodeToI18n(i18next.language || cultureLanguageCode);

  if (cultureLang === i18nLang) return;

  const matchingLanguage = availableLanguages.find(
    (language) => languageCodeToI18n(language.code) === i18nLang
  );

  if (matchingLanguage) {
    if (matchingLanguage.code === cultureLanguageCode) return;
    store.dispatch(
      setCulture({
        LanguageCode: matchingLanguage.code,
        CultureCode: matchingLanguage.code.split("-")[1] || "US",
        CurrencyCode: hotel.Culture.CurrencyCode,
      })
    );
    return;
  }

  syncI18nWithCulture(cultureLanguageCode);
};

export const hotelLanguages = (HotelCode: string) => {
  return languages.filter((c) => c.property.includes(HotelCode));
};

export const defaultLanguage = () => {
  const { hotel } = store.getState();
  const languages = hotelLanguages(hotel.HotelCode);
  const language = languages.find(
    (c: ILanguage) => c.code === hotel.Culture.LanguageCode
  );
  if (language) return language;

  // Default, return first language
  return languages[0];
};

export const findLanguageByCode = (code: string) => {
  const { hotel } = store.getState();
  const languages = hotelLanguages(hotel.HotelCode);
  const language = languages.find((c: ILanguage) => c.code === code);
  
  if (language) return language;

  // Default
  return defaultLanguage();
};

export const currentLanguageName = () => {
  const language = defaultLanguage();
  return language.name;
};
export const currentLanguageFlag = () => {
  const language = defaultLanguage();
  return language.flag;
};

export const getLanguageOptions = (): { [key: string]: string } => {
  const { hotel } = store.getState();
  const languages = hotelLanguages(hotel.HotelCode);
  const options: { [key: string]: string } = {};
  languages.forEach((language) => {
    options[language.code] = `${language.flag} ${language.name}`;
  });
  return options;
};

export const camalize = (str: string): string => {
  if (!str) return "";
  const camelized = str
    .toLowerCase()
    .replace(/[^a-zA-Z0-9]+(.)/g, (m, chr) => { 
      if (+m === 0) return "";
      return chr.toUpperCase(); 
    });
    
  return camelized;
};

export const firstTwoSentences = (str: string): string => {
  if (!str) return "";
  const sentences = str.match(/[^\.!\?]+[\.!\?]+/g);
  if (!sentences) return str;
  return sentences.slice(0, 2).join(" ");
};
